<template>
  <div>
    <div class="content-block dx-card responsive-paddings">
      <dx-form
        id="form"
        label-location="left"
        :form-data="user"
        :col-count="1"
        :read-only="loading"
        @initialized="saveFormInstance"
        validation-group="userValidation"
      >
        <dx-group-item
          :caption="$t('user.account')"
        >
          <dx-simple-item v-if="!isCloudMode" data-field="login"
            :editor-options="{inputAttr: {autocomplete:'off'}, maxLength: 128}"
          >
            <dx-label :text="$t('user.details.login')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-string-length-rule :min="3" :message="$t('general.min3chars')" />
            <dx-validation-rule type="custom" :validation-callback="loginValidation" :message="$t('general.badLogin')" />
          </dx-simple-item>

          <dx-simple-item data-field="email"
            :editor-options="editorsOptions.email"
          >
            <dx-label :text="$t('user.details.email')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="pattern" :pattern="validationPatterns.email" :message="$t('general.badEmail')" />
          </dx-simple-item>

          <dx-simple-item data-field="password"
            :editor-options="editorsOptions.password"
          >
            <dx-label :text="$t('user.details.password')" :show-colon="false" />
            <dx-validation-rule type="pattern" :pattern="validationPatterns.password" :message="$t('general.badPassword')" />
            <dx-required-rule v-if="isNew" :message="$t('general.mandatory')" />
            <dx-string-length-rule v-if="isNew" :min="8" :max="20" :message="$t('general.badLengthPassword')" />
          </dx-simple-item>

          <dx-simple-item data-field="passwordConfirmation"
            name="passwordConfirmation"
            :editor-options="editorsOptions.passwordConfirmation"
          >
            <dx-label :text="$t('user.details.passwordConfirmation')" :show-colon="false" />
            <dx-required-rule v-if="isNew" :message="$t('general.mandatory')" />
            <dx-compare-rule :comparison-target="passwordComparison" :message="$t('user.details.msgPasswordsDoNotMatch')" />
            <dx-string-length-rule v-if="isNew" :min="8" :max="20" :message="$t('general.badLengthPassword')" />
          </dx-simple-item>

          <dx-simple-item data-field="activeFlag" editor-type="dxCheckBox">
            <dx-label :text="$t('user.details.activeUser')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item data-field="isSystem" editor-type="dxCheckBox">
            <dx-label :text="$t('user.details.recipientSystemNotifications')" :show-colon="false" />
          </dx-simple-item>
        </dx-group-item>

        <dx-group-item
          :caption="$t('user.personalData')"
        >
          <dx-simple-item data-field="lastName" :editor-options="editorsOptions.lastName">
            <dx-label :text="$t('user.personalDataDetails.lastName')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
          </dx-simple-item>

          <dx-simple-item data-field="firstName" :editor-options="editorsOptions.firstName">
            <dx-label :text="$t('user.personalDataDetails.firstName')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item data-field="middleName" :editor-options="editorsOptions.middleName">
            <dx-label :text="$t('user.personalDataDetails.middleName')" :show-colon="false" />
          </dx-simple-item>
        </dx-group-item>

        <dx-group-item :caption="$t('user.locations')">
          <dx-simple-item editor-type="dxTagBox"
            :isRequired="!loadingVisible"
            :editor-options="editorsOptions.locations"
          >
            <dx-label :text="$t('user.locationsDetails.location')" />
          </dx-simple-item>
        </dx-group-item>

        <dx-group-item
          :caption="$t('user.roles')"
        >
          <!-- eslint-disable vue/no-unused-vars -->
          <div slot-scope="_" class="roles">
            <dx-check-box v-for="role in roles && roles.roles" v-bind:key="role && role.id"
              :value.sync="roles.userRoles[role.code]"
              :text="role.name"
              :disabled="appUser.id === userId"
            />
          </div>
          <!-- eslint-enable -->
        </dx-group-item>

        <dx-group-item>
          <!-- eslint-disable vue/no-unused-vars -->
          <div slot-scope="_" class="app-form-buttons">
            <aims2-button
              icon="save"
              type="default"
              :text="$t('user.actions.save')"
              :disabled="loading || hasGrantsForEdit"
              :busy="saving"
              @click="onSave"
            />

            <aims2-button
              icon="close"
              type="normal"
              :text="$t('user.actions.cancel')"
              :disabled="loading"
              @click="onCancel"
            />

            <aims2-button
              icon="trash"
              type="danger"
              :text="$t('user.actions.delete')"
              :disabled="hasDeleteGrants"
              :busy="deleting"
              @click="onDelete"
            />
          </div>
          <!-- eslint-enable -->
        </dx-group-item>
      </dx-form>

      <dx-load-panel :visible="loadingVisible" :position="{ of: '#form' }" />
    </div>
  </div>
</template>

<script>
import baseView from "../base.vue";
import appInfo from "../../app-info";

import {
  DxForm,
  DxSimpleItem,
  DxLabel,
  DxGroupItem,
  DxRequiredRule,
  DxCompareRule,
  DxStringLengthRule,
  DxValidationRule,
} from 'devextreme-vue/form';
import { DxCheckBox } from 'devextreme-vue';
import { DxLoadPanel } from "devextreme-vue/load-panel";
import Validator from 'devextreme/ui/validator';
import { custom } from 'devextreme/ui/dialog';
import ValidationEngine from 'devextreme/ui/validation_engine';

import { notifier } from '@/shared/lib/notifier/index';

import { getUser, getManagedRolesV, postUser, putUser, deleteUser, getUserLocations } from "@/services/api";
import aims2Button from "@/components/active-button.vue";
import showConfirmDialog from "@/components/confirm-dialog";

import { validationPatterns } from "@/utils/validation-patterns";
import eyeOpen from "@/assets/eyeopen.svg";
import eyeClose from "@/assets/eyeclose.svg";

import _ from "underscore";
import { intersectionWith, cloneDeep, xorBy, isEqual } from 'lodash';

function UserRoles(user, roles) {
  this.user = user;
  this.user.grants = this.user.grants || [];
  this.roles = roles;
  this.roleMap = {};
  this.userRoles = {};
  roles.forEach(role => {
    this.roleMap[role.code] = role;
    this.userRoles[role.code] = false;
  });
  this.user.grants.forEach(grant => {
    this.userRoles[grant.role.code] = true;
  });
}
UserRoles.prototype.updateGrants = function() {
  // Выбранные гранты не меняются
  this.user.grants = this.user.grants.filter(grant => this.userRoles[grant.role.code]);
  // Оставим в выбранных только новые (добавленные) роли
  const userRoles = cloneDeep(this.userRoles);
  this.user.grants.forEach(grant => delete userRoles[grant.role.code]);
  // Для каждой из этих новых добавим грант
  _.keys(userRoles).forEach(roleCode => {
    if (userRoles[roleCode]) {
      this.user.grants.push({
        role: this.roleMap[roleCode]
      });
    }
  });
}

export default {
  props: ["id"],
  extends: baseView,
  components: {
    DxForm, DxSimpleItem, DxLabel, DxGroupItem, DxCheckBox,
    DxRequiredRule, DxCompareRule, DxStringLengthRule, DxValidationRule, aims2Button,
    DxLoadPanel
  },
  data() {
    return {
      validationPatterns,
      formInstance: null,
      locationsDict: null,
      loading: false,
      saving: false,
      deleting: false,
      isCloudMode: this.$store.getters.isCloudMode,
      user: null,
      roles: null,
      passwordMode: 'password',
      confirmPasswordMode: 'password',
      passwordIcon: eyeClose,
      confirmPasswordIcon: eyeClose,
      loadingVisible: false,
      selectedRoles: []
    };
  },
  computed: {
    userId() {
      return Number(this.$route.params.id);
    },
    isNew() {
      return this.userId < 0;
    },
    appUser() {
      return this.$store.getters.user;
    },
    hasDeleteGrants() {
      return this.appUser.id === this.userId || this.loading || (this.user && !this.user.id) || this.hasGrantsForEdit;
    },
    activeRoles() {
      if (this.user && this.user.locations) {
        return this.user.locations.map((location) => {
          if (this.locationsDict.some(e => e.code === location.code)) {
            return location.id;
          }
        })
      } else {
        return this.locationsDict && this.locationsDict.filter(location => this.selectedRoles.includes(location.id)).map(l => l.id);
      }
    },
    editorsOptions() {
      return {
        email: {inputAttr: {autocomplete:'off'}, maxLength: 256},
        password: {
          mode: this.passwordMode,
          inputAttr: { autocomplete:'new-password' },
          maxLength: 20,
          buttons : [{
            name: 'password',
            options:{
              icon: this.passwordIcon,
              stylingMode: 'text',
              type: 'default',
              onClick: () => {
                // eslint-disable-next-line
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
                // eslint-disable-next-line
                this.passwordIcon = this.passwordIcon === eyeClose ? eyeOpen : eyeClose;
              }
            }
          }],
          onValueChanged: () => {
            const editor = this.formInstance.getEditor('passwordConfirmation');
            if (editor.option('value')) {
              const instance = Validator.getInstance(editor.element());
              instance.validate();
            }
          },
        },
        passwordConfirmation: {
          mode: this.confirmPasswordMode,
          inputAttr: { autocomplete:'new-password' },
          maxLength: 20,
          buttons : [{
            name: 'passwordConfirmation',
            options:{
              icon: this.confirmPasswordIcon,
              stylingMode: 'text',
              type: 'default',
              onClick: () => {
                // eslint-disable-next-line
                this.confirmPasswordMode = this.confirmPasswordMode === 'text' ? 'password' : 'text';
                // eslint-disable-next-line
                this.confirmPasswordIcon = this.confirmPasswordIcon === eyeClose ? eyeOpen : eyeClose;
              }
            }
          }],
        },
        lastName: {maxLength: 128},
        firstName: {maxLength: 128},
        middleName: {maxLength: 128},
        locations: {
          dataSource: this.locationsDict,
          value: this.activeRoles,
          disabled: this.loadingVisible,
          displayExpr: 'name',
          valueExpr: 'id',
          key: 'id',
          onValueChanged: this.onTagBoxValueChanged,
          showSelectionControls: true,
          maxDisplayedTags: 6
        }
      };
    },
    hasGrantsForEdit() {
      if (this.locationsDict && this.user) {
        if (this.user.locations)
          return intersectionWith(this.locationsDict, this.user.locations, (a, b) => a.code === b.code).length === 0;
        else
          return false;
      } else {
        return true;
      }
    }
  },
  methods: {
    loginValidation({ value }) {
      if (!value) return true;

      const isLogin = validationPatterns.login.test(value);
      const isEmail = validationPatterns.email.test(value);

      return isLogin || isEmail;
    },
    onTagBoxValueChanged(e) {
      if (!isEqual(e.value, this.selectedRoles)) {
        this.selectedRoles = e.value
      }
    },
    saveFormInstance(e) {
      this.formInstance = e.component;
    },
    passwordComparison() {
      if (this.user.password !== undefined)
        return this.user.password;
      else
        return false;
    },
    validate() {
      return ValidationEngine.validateGroup("userValidation");
    },
    isSelectedRoles() {
      const activeRoles = Object.values(this.roles.roles).map(role => this.roles.userRoles[role.code]);
      if (activeRoles.filter(Boolean).length > 0) {
        return true;
      } else {
        const myDialog = custom({
          dragEnabled: false,
          title: this.$t('general.warning'),
          messageHtml: this.$t('user.warning'),
          buttons: [
            {
              text:this.$t('general.proceed'),
              onClick: () => false
            },
          ]
        });
        myDialog.show();

        return false;
      }
    },
    async loadUser() {
      this.loadingVisible = true;
      try {
        const roles = await getManagedRolesV();

        if (!this.isNew) {
          const user = await getUser(this.userId);
          this.roles = new UserRoles(user, roles);
          this.user = user;
        } else {
          const user = { activeFlag: true };
          this.roles = new UserRoles(user, roles);
          this.user = user;
        }
        this.$store.commit("setBreadcrumb", [{
          link: "/users",
          title: `${this.$t('users.title')}`
        }, {
          link: this.userId? `/user/${this.userId}`: "/user",
          title: this.user.login||`${this.$t('user.title')}`
        }]);
      } catch(err) {
        this.onDataError(err);
      }
      this.loadingVisible = false;
    },
    async onSave() {
      const validate = this.validate();
      if (!validate.isValid) {
        validate.brokenRules[0].validator.focus();  
        return;
      }

      if (!this.isSelectedRoles()) {
        return;
      }

      this.loading = true;
      this.saving = true;
      try {
        this.roles.updateGrants();

        this.user.locations = this.locationsDict.filter(l => this.selectedRoles.includes(l.id));
        let user = { ...this.user, passwordHash: this.user.password };
        if (this.isCloudMode)
          user.login = user.email;
        delete user.password;
        delete user.passwordConfirmation;
        user = await (user.id? putUser: postUser)(user);

        let remoteLocations = [];
        if (user.locations.length >= this.user.locations.length)
          remoteLocations = xorBy(user.locations, this.user.locations, 'code');
        else
          remoteLocations = xorBy(this.user.locations, user.locations, 'code');

        const listLocations = remoteLocations.map(l => ` <strong>${l.name}</strong>`);
        const html = this.$t('user.userRemoteLocations1') + listLocations + `.<br>${this.$t('user.userRemoteLocations2')}`;

        const myDialog = custom({
          dragEnabled: false,
          title: this.$t('general.warning'),
          messageHtml: `<center class="dialog-message">${html}</center>`,
          buttons: [
            {
              text:this.$t('general.proceed'),
              onClick: () => {
                notifier.showSuccess(this.$t('user.userNotifySaveSuccess'));
                if (this.$router.referer) {
                  this.$router.push({
                    name: "users",
                    query: _.extend({}, this.$router.referer.query, {
                      _flash: user.id
                    })
                  });
                } else {
                  this.$router.push({ name: "users" });
                }
              }
            },
          ]
        });

        if (remoteLocations.length > 0) {
          myDialog.show().then(() => { return });
        } else {
          notifier.showSuccess(this.$t('user.userNotifySaveSuccess'));
          if (this.$router.referer) {
            this.$router.push({
              name: "users",
              query: _.extend({}, this.$router.referer.query, {
                _flash: user.id
              })
            });
          } else {
            this.$router.push({ name: "users" });
          }
        }
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.loading = false;
        this.saving = false;
      }
    },
    onCancel() {
      this.$router.go(-1);
    },
    async onDelete() {
      const response = await showConfirmDialog({
        title: `${this.$t('user.deletetitle')}`,
        message: `${this.$t('user.user')} "${this.user.login}" ${this.$t('user.userNotifyDelete')}`,
        btnType: "danger",
        okButton: this.$t('general.proceed'),
        cancelButton: this.$t('general.cancel'),
      });

      if (response) {
        this.loading = true;
        this.deleting = true;
        try {
          await deleteUser(this.user.id);
          notifier.showSuccess(this.$t('user.userNotifyDeleteSuccess'));
          if (this.$router.referer) {
            this.$router.push({
              name: "users",
              query: _.extend({}, this.$router.referer.query)
            });
          } else {
            this.$router.push({ name: "users" });
          }
        } catch (err) {
          this.onDataError(err);
        } finally {
          this.loading = false;
          this.deleting = false;
        }
      }
    }
  },
  async created() {
    document.title = `${appInfo.title} - ${this.$t('user.users')}`;
    this.locationsDict = await getUserLocations();
    this.loadUser();
  }
};
</script>

<style lang="scss">
.roles {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 16px;
}

.dx-item {
  margin-bottom: 10px;
}
.dx-invalid-message-content {
  padding: 0 10px !important;
}

.dialog-message {
  width: 500px;
  white-space: break-spaces;
}
</style>
